import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  useSubscriptions,
  useAllowedActions,
  usePaymentMethods,
  useCustomersSummaryByIds,
  useLocations,
  useSubscriptionStatuses,
} from '@/hooks/useData';
import { usePermission } from '@/hooks/usePermission';
import { RefreshCw, ChevronDown, ChevronLeft, ShieldAlert, Lock, Key, ShieldCheck, AlertCircle } from 'lucide-react';

const statusColors: Record<string, { bg: string; color: string; border: string }> = {
  active: { bg: 'rgba(16,185,129,0.15)', color: '#34d399', border: 'rgba(16,185,129,0.4)' },
  inactive: { bg: 'rgba(100,116,139,0.15)', color: '#94a3b8', border: 'rgba(100,116,139,0.4)' },
  suspended: { bg: 'rgba(245,158,11,0.15)', color: '#fbbf24', border: 'rgba(245,158,11,0.4)' },
  terminated: { bg: 'rgba(239,68,68,0.15)', color: '#f87171', border: 'rgba(239,68,68,0.4)' },
  pending: { bg: 'rgba(99,102,241,0.15)', color: '#818cf8', border: 'rgba(99,102,241,0.4)' },
};

const statusLabels: Record<string, string> = {
  active: 'نشط',
  inactive: 'غير نشط',
  suspended: 'موقوف',
  terminated: 'منتهي',
  pending: 'قيد التفعيل',
};

const translatePaymentMethod = (method: string) => {
  if (!method) return 'بطاقة دفع';
  const m = method.toLowerCase();
  if (m === 'wallet') return 'محفظة';
  if (m === 'card' || m === 'paymentcard' || m === 'payment_card' || m === 'creditcard' || m === 'credit_card' || m === 'بطاقة دفع') return 'بطاقة دفع';
  if (m === 'cash') return 'نقداً';
  return method;
};

const demoSubscriptions: any[] = [];

const SubscriptionsPage: React.FC = () => {
  const { t } = useTranslation();
  const { hasPermission } = usePermission();
  const canManageSubscriptions = hasPermission('subscriptions.manage');
  const [showMenu, setShowMenu] = React.useState(false);
  const [pageNumber, setPageNumber] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(10);

  const { data: rawData, isLoading, refetch, isRefetching } = useSubscriptions({
    pageNumber,
    pageSize,
  });

  const { subscriptionsData, totalItems } = React.useMemo(() => {
    const unwrapped = rawData;
    const items = (unwrapped as any)?.items || (unwrapped as any)?.data;
    const count = (unwrapped as any)?.totalCount || (unwrapped as any)?.total;
    return {
      subscriptionsData: Array.isArray(items) ? items : [],
      totalItems: count || (Array.isArray(items) ? items.length : 0),
    };
  }, [rawData]);

  // Extract subscription IDs and customer IDs for current page
  const subscriptionIds = React.useMemo(() => {
    return subscriptionsData.map((item: any) => item.self?.id || item.id).filter(Boolean);
  }, [subscriptionsData]);

  const customerIds = React.useMemo(() => {
    const ids = subscriptionsData.map((item: any) => item.customer?.id || item.customerId).filter(Boolean);
    return Array.from(new Set(ids));
  }, [subscriptionsData]);

  // Trigger parallel API calls using custom hooks
  const { data: allowedActionsRes } = useAllowedActions(subscriptionIds, !isLoading);
  const { data: paymentMethodsRes } = usePaymentMethods(subscriptionIds, !isLoading);
  const { data: customerSummaryRes } = useCustomersSummaryByIds(customerIds, !isLoading);
  const { data: zonesRes } = useLocations();
  const { data: statusesRes } = useSubscriptionStatuses();

  // Create lookups
  const customerNameMap = React.useMemo(() => {
    const map: Record<string, string> = {};
    const items = (customerSummaryRes as any)?.items || (customerSummaryRes as any)?.data || customerSummaryRes;
    if (Array.isArray(items)) {
      items.forEach((cust: any) => {
        if (cust.id) {
          map[String(cust.id)] = cust.displayValue || '—';
        }
      });
    }
    return map;
  }, [customerSummaryRes]);

  const paymentMethodMap = React.useMemo(() => {
    const map: Record<string, string> = {};
    const items = (paymentMethodsRes as any)?.items || (paymentMethodsRes as any)?.data || paymentMethodsRes;
    if (Array.isArray(items)) {
      items.forEach((pm: any) => {
        if (pm.subscriptionId) {
          map[String(pm.subscriptionId)] = pm.paymentMethod?.displayValue || '—';
        }
      });
    }
    return map;
  }, [paymentMethodsRes]);

  const allowedActionsMap = React.useMemo(() => {
    const map: Record<string, string[]> = {};
    const items = (allowedActionsRes as any)?.items || (allowedActionsRes as any)?.data || allowedActionsRes;
    if (Array.isArray(items)) {
      items.forEach((item: any) => {
        if (item.subscriptionId) {
          const actions = Array.isArray(item.allowedActions)
            ? item.allowedActions.map((act: any) => act.displayValue)
            : [];
          map[String(item.subscriptionId)] = actions;
        }
      });
    }
    return map;
  }, [allowedActionsRes]);

  const subscriptions = React.useMemo(() => {
    if (isLoading) return [];
    if (subscriptionsData.length === 0) return [];

    return subscriptionsData.map((item: any) => {
      const isExpired = item.status === 'Expired';
      const isSuspended = item.status === 'Suspended' || item.isSuspended;
      let uiStatus = 'active';
      if (isExpired) uiStatus = 'terminated';
      else if (isSuspended) uiStatus = 'suspended';
      
      const speed = item.services?.find((s: any) => s.type?.displayValue === 'Base')?.displayValue || item.services?.[0]?.displayValue || 'Mbps 40';
      const price = item.monthlyFee ? Number(item.monthlyFee).toLocaleString() : '35,000';

      const subId = item.self?.id || item.username || '—';
      const custId = item.customer?.id || item.customerId;
      const customerName = customerNameMap[String(custId)] || item.customer?.displayValue || '—';
      const paymentMethod = paymentMethodMap[String(subId)] || item.paymentMode || 'Card';
      const actions = allowedActionsMap[String(subId)] || [];

      return {
        id: subId,
        customer: customerName,
        package: item.bundle?.displayValue || item.bundleId || '—',
        speed: speed,
        price: price,
        paymentMethod: paymentMethod,
        currency: 'IQD',
        expiry: item.expires ? new Date(item.expires).toLocaleDateString('ar-IQ') : '—',
        status: uiStatus,
        allowedActions: actions,
      };
    });
  }, [subscriptionsData, isLoading, customerNameMap, paymentMethodMap, allowedActionsMap]);

  return (
    <div style={{ padding: '20px', direction: 'rtl', minHeight: '100%' }}>
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @media (max-width: 768px) {
          .subscriptions-header {
            flex-direction: column !important;
            align-items: stretch !important;
            gap: 16px !important;
          }
          .subscriptions-header-button {
            width: 100% !important;
            justify-content: center !important;
            padding: 10px !important;
          }
        }
      `}</style>

      {/* Page header */}
      <div
        className="subscriptions-header"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '20px',
        }}
      >
        <div style={{ textAlign: 'right' }}>
          <h1 style={{ color: '#e2e8f0', fontSize: '22px', fontWeight: 700, marginBottom: '4px' }}>
            {t('subscriptions.title') || 'إدارة الاشتراكات'}
          </h1>
          <p style={{ color: '#64748b', fontSize: '13px' }}>
            {t('common.total') || 'الإجمالي'} {totalItems}
          </p>
        </div>
        <button
          onClick={() => refetch()}
          className="subscriptions-header-button"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            background: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: '8px',
            padding: '8px 14px',
            color: '#94a3b8',
            cursor: 'pointer',
            fontSize: '13px',
          }}
        >
          <RefreshCw size={14} style={isRefetching ? { animation: 'spin 1s linear infinite' } : {}} />
          {t('common.refresh') || 'تحديث'}
        </button>
      </div>

      {/* Dropdown filter */}
      <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: '16px' }}>
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => setShowMenu(!showMenu)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              background: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: '8px',
              padding: '8px 14px',
              color: '#94a3b8',
              cursor: 'pointer',
              fontSize: '13px',
              minWidth: '200px',
              justifyContent: 'space-between',
            }}
          >
            <ChevronDown size={14} />
            <span>إدارة الاشتراكات</span>
          </button>
        </div>
      </div>

      {/* Table */}
      <div
        style={{
          background: 'rgba(13,31,60,0.7)',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: '12px',
          overflowX: 'auto',
        }}
      >
        <table className="responsive-table" style={{ width: '100%', borderCollapse: 'collapse', direction: 'rtl' }}>
          <thead>
            <tr style={{ background: 'rgba(255,255,255,0.04)' }}>
              {['رقم الاشتراك', 'العميل', 'الباقة', 'السرعة', 'السعر', 'طريقة الدفع', 'تاريخ الانتهاء', 'الحالة', 'الإجراءات المتاحة'].map((col) => (
                <th
                  key={col}
                  style={{
                    padding: '12px 16px',
                    textAlign: 'right',
                    color: '#64748b',
                    fontSize: '12px',
                    fontWeight: 600,
                    borderBottom: '1px solid rgba(255,255,255,0.08)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={9} style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
                  جاري التحميل...
                </td>
              </tr>
            ) : subscriptions.length === 0 ? (
              <tr>
                <td colSpan={9} style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
                  لا توجد اشتراكات متوفرة
                </td>
              </tr>
            ) : (
              subscriptions.map((sub: any, i: number) => {
                const sc = statusColors[sub.status] || statusColors.inactive;
                const isActive = sub.status === 'active';
                const isSuspended = sub.status === 'suspended';
                const isTerminated = sub.status === 'terminated';

                const canRenew = sub.allowedActions.includes('Renew');
                const canSuspend = sub.allowedActions.includes('Suspend');
                const canResume = sub.allowedActions.includes('Resume');

                return (
                  <tr
                    key={sub.id || i}
                    style={{ borderBottom: '1px solid rgba(255,255,255,0.04)', transition: 'background 0.15s' }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.03)';
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLElement).style.background = 'transparent';
                    }}
                  >
                    <td data-label="رقم الاشتراك" style={{ padding: '12px 16px', color: '#94a3b8', fontSize: '13px', whiteSpace: 'nowrap' }}>
                      {sub.id}
                    </td>
                    <td data-label="العميل" style={{ padding: '12px 16px', color: '#e2e8f0', fontSize: '13px', fontWeight: 500 }}>
                      {sub.customer}
                    </td>
                    <td data-label="الباقة" style={{ padding: '12px 16px', color: '#94a3b8', fontSize: '13px' }}>
                      {sub.package}
                    </td>
                    <td data-label="السرعة" style={{ padding: '12px 16px', color: '#94a3b8', fontSize: '13px' }}>
                      {sub.speed}
                    </td>
                    <td data-label="السعر" style={{ padding: '12px 16px', color: '#94a3b8', fontSize: '13px', direction: 'ltr', textAlign: 'right' }}>
                      {sub.currency} {sub.price}
                    </td>
                    <td data-label="طريقة الدفع" style={{ padding: '12px 16px', color: '#94a3b8', fontSize: '13px' }}>
                      <span
                        style={{
                          background: 'rgba(255,255,255,0.04)',
                          border: '1px solid rgba(255,255,255,0.08)',
                          padding: '3px 8px',
                          borderRadius: '6px',
                          fontSize: '11px',
                          color: '#cbd5e1'
                        }}
                      >
                        {translatePaymentMethod(sub.paymentMethod)}
                      </span>
                    </td>
                    <td data-label="تاريخ الانتهاء" style={{ padding: '12px 16px', color: '#64748b', fontSize: '13px', direction: 'ltr', textAlign: 'right' }}>
                      {sub.expiry}
                    </td>
                    <td data-label="الحالة" style={{ padding: '12px 16px' }}>
                      <span
                        style={{
                          background: sc.bg,
                          color: sc.color,
                          border: `1px solid ${sc.border}`,
                          padding: '3px 10px',
                          borderRadius: '20px',
                          fontSize: '12px',
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {statusLabels[sub.status] || sub.status}
                      </span>
                    </td>
                    <td data-label="الإجراءات المتاحة" style={{ padding: '12px 16px' }}>
                      <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                        {(isActive || isSuspended) && (
                          <button
                            disabled={!canManageSubscriptions || !canRenew}
                            title={!canManageSubscriptions ? '🔒 غير مصرح (يتطلب صلاحية subscriptions.manage)' : (!canRenew ? 'غير مسموح لهذا الاشتراك حالياً' : '')}
                            style={{
                              background: 'rgba(59,130,246,0.15)',
                              border: '1px solid rgba(59,130,246,0.3)',
                              borderRadius: '6px',
                              padding: '4px 10px',
                              color: '#60a5fa',
                              cursor: (canManageSubscriptions && canRenew) ? 'pointer' : 'not-allowed',
                              opacity: (canManageSubscriptions && canRenew) ? 1 : 0.4,
                              fontSize: '11px',
                              fontWeight: 600,
                              whiteSpace: 'nowrap',
                              transition: 'all 0.2s',
                            }}
                          >
                            {!canManageSubscriptions ? '🔒 تجديد' : 'تجديد'}
                          </button>
                        )}
                        {isActive && (
                          <button
                            disabled={!canManageSubscriptions || !canSuspend}
                            title={!canManageSubscriptions ? '🔒 غير مصرح (يتطلب صلاحية subscriptions.manage)' : (!canSuspend ? 'غير مسموح لهذا الاشتراك حالياً' : '')}
                            style={{
                              background: 'rgba(245,158,11,0.15)',
                              border: '1px solid rgba(245,158,11,0.3)',
                              borderRadius: '6px',
                              padding: '4px 10px',
                              color: '#fbbf24',
                              cursor: (canManageSubscriptions && canSuspend) ? 'pointer' : 'not-allowed',
                              opacity: (canManageSubscriptions && canSuspend) ? 1 : 0.4,
                              fontSize: '11px',
                              fontWeight: 600,
                              whiteSpace: 'nowrap',
                              transition: 'all 0.2s',
                            }}
                          >
                            {!canManageSubscriptions ? '🔒 إيقاف مؤقت' : 'إيقاف مؤقت'}
                          </button>
                        )}
                        {(isTerminated || isSuspended) && (
                          <button
                            disabled={!canManageSubscriptions || !canResume}
                            title={!canManageSubscriptions ? '🔒 غير مصرح (يتطلب صلاحية subscriptions.manage)' : (!canResume ? 'غير مسموح لهذا الاشتراك حالياً' : '')}
                            style={{
                              background: 'rgba(16,185,129,0.15)',
                              border: '1px solid rgba(16,185,129,0.3)',
                              borderRadius: '6px',
                              padding: '4px 10px',
                              color: '#34d399',
                              cursor: (canManageSubscriptions && canResume) ? 'pointer' : 'not-allowed',
                              opacity: (canManageSubscriptions && canResume) ? 1 : 0.4,
                              fontSize: '11px',
                              fontWeight: 600,
                              whiteSpace: 'nowrap',
                              transition: 'all 0.2s',
                            }}
                          >
                            {!canManageSubscriptions ? '🔒 تفعيل' : 'تفعيل'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>

        {/* Pagination Controls */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 16px',
            background: 'rgba(255,255,255,0.02)',
            borderTop: '1px solid rgba(255,255,255,0.08)',
            direction: 'rtl',
            flexWrap: 'wrap',
            gap: '12px',
          }}
        >
          {/* Page size selector */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ color: '#64748b', fontSize: '13px' }}>عرض:</span>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPageNumber(1);
              }}
              style={{
                background: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(255,255,255,0.12)',
                borderRadius: '6px',
                padding: '4px 8px',
                color: '#e2e8f0',
                fontSize: '13px',
                outline: 'none',
                cursor: 'pointer',
              }}
            >
              {[10, 20, 50, 100].map((size) => (
                <option key={size} value={size} style={{ background: '#0d1f3c', color: '#e2e8f0' }}>
                  {size}
                </option>
              ))}
            </select>
          </div>

          {/* Page navigation */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <button
              onClick={() => setPageNumber((prev) => Math.max(prev - 1, 1))}
              disabled={pageNumber === 1 || isLoading}
              style={{
                background: pageNumber === 1 ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(255,255,255,0.12)',
                borderRadius: '8px',
                padding: '6px 14px',
                color: pageNumber === 1 ? '#475569' : '#e2e8f0',
                cursor: pageNumber === 1 ? 'not-allowed' : 'pointer',
                fontSize: '13px',
                fontWeight: 600,
                transition: 'all 0.2s',
              }}
            >
              السابق
            </button>
            
            <span style={{ color: '#94a3b8', fontSize: '13px' }}>
              الصفحة {pageNumber} من {Math.max(Math.ceil(totalItems / pageSize), 1)}
            </span>

            <button
              onClick={() => setPageNumber((prev) => Math.min(prev + 1, Math.max(Math.ceil(totalItems / pageSize), 1)))}
              disabled={pageNumber >= Math.max(Math.ceil(totalItems / pageSize), 1) || isLoading}
              style={{
                background: pageNumber >= Math.max(Math.ceil(totalItems / pageSize), 1) ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(255,255,255,0.12)',
                borderRadius: '8px',
                padding: '6px 14px',
                color: pageNumber >= Math.max(Math.ceil(totalItems / pageSize), 1) ? '#475569' : '#e2e8f0',
                cursor: pageNumber >= Math.max(Math.ceil(totalItems / pageSize), 1) ? 'not-allowed' : 'pointer',
                fontSize: '13px',
                fontWeight: 600,
                transition: 'all 0.2s',
              }}
            >
              التالي
            </button>
          </div>

          {/* Total stats */}
          <div style={{ color: '#64748b', fontSize: '13px' }}>
            يظهر {totalItems === 0 ? 0 : (pageNumber - 1) * pageSize + 1}-{Math.min(pageNumber * pageSize, totalItems)} من أصل {totalItems} مشترك
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionsPage;
